import { Box } from '@rebass/grid/emotion'
import {
  EntityList,
  EntityListProps,
  renderDate,
  renderDefault,
  renderPath,
  renderPersona,
} from 'components/EntityList'
import { LicensePlateType, Vehicle, VehicleType } from 'model'
import {
  ConstrainMode,
  IColumn,
  PersonaSize,
  SelectionMode,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'
import {
  GroupType,
  ResourceOfficeBookingListItem,
  ResourceVehicleBookingListItem,
} from './index'

export type ResourceOfficeViewListProps =
  EntityListProps<ResourceOfficeBookingListItem> & { groupType: GroupType }

export const ResourceOfficeBookingsViewList: React.FunctionComponent<
  ResourceOfficeViewListProps
> = ({ items, groupType, ...props }) => {
  const { t } = useTranslation()

  const fieldColumn = createFieldColumn(t)
  const columns = [
    groupType === GroupType.OfficeLocation
      ? fieldColumn('parent.title', {
          onRender: (item: ResourceOfficeBookingListItem, i, col) => {
            return (
              <Box key={i} style={{ whiteSpace: 'pre-line' }}>
                {item.resource.parent
                  ? t(`field.type.${item.resource.type}`)
                  : t(`field.type.${item.resource.type}`)}
              </Box>
            )
          },
          getGroupValue: (item: ResourceOfficeBookingListItem) => {
            return item.resource.parent
              ? `${item.resource.parent!.title}`
              : t(`field.type.${item.resource.type}`)
          },
          isSorted: true,

          ungroupable: true,
          minWidth: 120,
          isGrouped: true,
        })
      : fieldColumn('provingGround', {
          onRender: (item: ResourceOfficeBookingListItem, i, col) => {
            return (
              <Box key={i} style={{ whiteSpace: 'pre-line' }}>
                {t(`field.type.${item.resource.type}`)}
              </Box>
            )
          },
          getGroupValue: (item: ResourceOfficeBookingListItem) => {
            return item.provingGround
              ? item.provingGround
              : t(`field.type.${item.resource.type}`)
          },

          ungroupable: true,
          minWidth: 120,
          isGrouped: true,
        }),
    fieldColumn('dateFrom', {
      onRender: renderDate,

      ungroupable: true,
    }),
    fieldColumn('dateTo', {
      onRender: renderDate,
      ungroupable: true,
    }),
    fieldColumn('resource.title', {
      onRender: renderPath,
      ungroupable: true,
      getGroupValue: (resource: ResourceOfficeBookingListItem) =>
        resource!.resource.title,
    }),
    fieldColumn('provingTitel', {
      ungroupable: true,
    }),
    fieldColumn('proving', {
      ungroupable: true,
    }),
    fieldColumn('responsible', {
      onRender: (item: any, i, col) => {
        if (item.responsible) {
          return renderPersona({
            size: PersonaSize.size24,
          })(item, i, col)
        }

        return (
          <Box key={i} style={{ whiteSpace: 'pre-line' }}>
            {''}
          </Box>
        )
      },
      minWidth: 155,
      ungroupable: true,
    }),
  ]

  return (
    <>
      <EntityList
        alternate={true}
        stickyHeader={true}
        selectionMode={SelectionMode.multiple}
        items={items}
        constrainMode={ConstrainMode.horizontalConstrained}
        columns={columns}
        onShouldVirtualize={() => false}
        parentGroup={
          groupType === GroupType.OfficeLocation ? 'provingGround' : 'dateFrom'
        }
        noParentGroupText={t('noGroup')}
        {...props}
      />
    </>
  )
}

export type ResourceVehicleViewListProps =
  EntityListProps<ResourceVehicleBookingListItem> & { groupType: GroupType }

export const ResourceVehicleBookingsViewList: React.FunctionComponent<
  ResourceVehicleViewListProps
> = ({ items, groupType, ...props }) => {
  const renderLabel = (item: any, i: any, col: any) => {
    if (item.unknown) {
      return t('model.proving.vehicle.vehicleUnknown.long')
    }
    if (item.type === VehicleType.Internal) {
      return item.label || (item.eqVehicle && item.eqVehicle.label) || ''
    } else {
      return item.labelExternal
    }
  }

  const renderModel = (item: any, i: any, col: any) => {
    if (item.type === VehicleType.External) {
      return t('enum.vehicleType.external')
    } else {
      return renderDefault(item, i, col)
    }
  }

  const renderLicensePlate = (item: Vehicle, i: any, col: any) => {
    if (
      item.licensePlateType &&
      item.licensePlateType === LicensePlateType.Unknown
    ) {
      return t('model.proving.vehicle.licensePlateUnknown')
    }
    if (
      item.licensePlateType &&
      item.licensePlateType === LicensePlateType.NoRegistration
    ) {
      return t('model.proving.vehicle.licensePlateNoRegistration')
    }
    if (item.unknown) {
      return t('model.proving.vehicle.vehicleUnknown.short')
    }
    return item.licensePlate || ((item as any).eqVehicle && (item as any).eqVehicle.licensePlate) || ''
  }

  const { t } = useTranslation()

  const fieldColumn = createFieldColumn(t)

  const columns: IColumn[] = [
    groupType === GroupType.VehicleModell
      ? fieldColumn('vehicleLabel', {
          isGrouped: true,
          getGroupValue: (item: any, col: IColumn) =>
            item.type === VehicleType.Internal
              ? item.model || t('model.proving.vehicle.modelUnknown')
              : t('enum.vehicleType.external'),
          onRender: renderLabel,
          minWidth: 130,
          ungroupable: true,
        })
      : fieldColumn('dateFrom', {
          isGrouped: true,
          getGroupValue: (item: any, col: IColumn) =>
            item.unknown
              ? t('model.proving.vehicle.vehicleUnknown.long')
              : item.type === VehicleType.Internal
              ? item.label
              : item.labelExternal,
          onRender: (item: any, i, col) => {
            return renderDate(item, i, col)
          },
          ungroupable: true,
          minWidth: 130,
        }),
  ] as IColumn[]

  if (groupType !== GroupType.VehicleNumber) {
    columns.push(
      fieldColumn('dateFrom', {
        onRender: renderDate,

        ungroupable: true,
      })
    )
  }

  columns.push(
    fieldColumn('dateTo', {
      onRender: renderDate,
      ungroupable: true,
    })
  )

  if (groupType !== GroupType.VehicleNumber) {
    columns.push(
      fieldColumn('licensePlate', {
        onRender: renderLicensePlate,
        ungroupable: true,
      })
    )
  } else {
    columns.push(
      fieldColumn('vehicleLabel', {
        onRender: renderLabel,
        ungroupable: true,
      }),
      fieldColumn('licensePlate', {
        onRender: renderLicensePlate,
        ungroupable: true,
      }),
      fieldColumn('model', {
        onRender: renderModel,
        ungroupable: true,
      })
    )
  }

  columns.push(
    fieldColumn('orderId', { ungroupable: true }),
    fieldColumn('provingTitel', {
      ungroupable: true,
    }),
    fieldColumn('proving', {
      ungroupable: true,
    }),
    fieldColumn('responsible', {
      onRender: (item: any, i, col) => {
        if (item.responsible) {
          return renderPersona({
            size: PersonaSize.size24,
          })(item, i, col)
        }

        return (
          <Box key={i} style={{ whiteSpace: 'pre-line' }}>
            {''}
          </Box>
        )
      },
      minWidth: 155,
      ungroupable: true,
    })
  )

  return (
    <>
      <EntityList
        alternate={true}
        stickyHeader={true}
        selectionMode={SelectionMode.multiple}
        items={items}
        constrainMode={ConstrainMode.horizontalConstrained}
        columns={columns}
        {...props}
      />
    </>
  )
}
